/**
 * Онбординг-трейс: кольцо последних событий + батч на бэкенд (один flow = один .jsonl).
 * При ошибке — отдельный запрос с хвостом из ring (до 30) + событие ошибки.
 */

export type OnboardingTraceApp = 'landing' | 'web-master' | 'web-booking' | 'backend' | 'mobile-master';

export type OnboardingTraceInput = {
  screen?: string;
  step?: string;
  event: string;
  result?: 'ok' | 'error';
  payload?: Record<string, unknown> | null;
  error?: string | null;
};

type StoredEvent = OnboardingTraceInput & { clientTs: string };

type InitConfig = {
  getApiBaseUrl: () => string;
  app: OnboardingTraceApp;
  getBuild: () => string;
  getUserId: () => string | null;
  /** Если не заданы — свои ключи в localStorage/sessionStorage */
  getAnonId?: () => string;
  getFlowId?: () => string;
};

const RING_MAX = 30;
const DEBOUNCE_MS = 2000;
const DEDUPE_WINDOW_MS = 10_000;
const REACT_ERROR_DEDUPE_WINDOW_MS = 30_000;
const RECENT_EVENT_KEYS_MAX = 300;
const MB_ONB_ANON = 'mb_onboarding_anon_id';
const MB_ONB_FLOW = 'mb_onboarding_flow_id';

let init: InitConfig | null = null;
const ring: StoredEvent[] = [];
const outbox: StoredEvent[] = [];
let debounceTimer: ReturnType<typeof setTimeout> | null = null;
const recentEventKeys = new Map<string, number>();
const startedFlowIds = new Set<string>();
/** Прокси/API вернул 401/403 — повторные POST только шумят в консоли и не меняют UI */
let traceEndpointAuthRejected = false;
let traceAuthRejectedWarned = false;

function safeLs(): Storage | null {
  if (typeof window === 'undefined') return null;
  try {
    return localStorage;
  } catch {
    return null;
  }
}

function safeSs(): Storage | null {
  if (typeof window === 'undefined') return null;
  try {
    return sessionStorage;
  } catch {
    return null;
  }
}

function randomFlow(): string {
  return `ob_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;
}

export function ensureOnboardingAnonId(): string {
  const ls = safeLs();
  const existing = ls?.getItem(MB_ONB_ANON);
  if (existing) return existing;
  const uuid =
    typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
      ? crypto.randomUUID()
      : `${Date.now()}_${Math.random().toString(36).slice(2)}`;
  const next = `an_${uuid}`;
  ls?.setItem(MB_ONB_ANON, next);
  return next;
}

export function ensureOnboardingFlowId(): string {
  const ss = safeSs();
  const existing = ss?.getItem(MB_ONB_FLOW);
  if (existing) return existing;
  const next = randomFlow();
  ss?.setItem(MB_ONB_FLOW, next);
  return next;
}

/** Новая попытка онбординга — новый flow-файл */
export function startNewOnboardingFlowId(): string {
  const next = randomFlow();
  safeSs()?.setItem(MB_ONB_FLOW, next);
  return next;
}

export function initOnboardingTrace(cfg: InitConfig): void {
  init = cfg;
}

function pushRing(ev: StoredEvent): void {
  ring.push(ev);
  while (ring.length > RING_MAX) ring.shift();
}

function getIds() {
  if (!init) {
    return { anonId: ensureOnboardingAnonId(), flowId: ensureOnboardingFlowId() };
  }
  return {
    anonId: init.getAnonId ? init.getAnonId() : ensureOnboardingAnonId(),
    flowId: init.getFlowId ? init.getFlowId() : ensureOnboardingFlowId(),
  };
}

function payloadSignature(payload: Record<string, unknown> | null | undefined): string {
  if (payload == null) return '';
  try {
    return JSON.stringify(payload).slice(0, 500);
  } catch {
    return '[payload_non_serializable]';
  }
}

function buildEventDedupeKey(flowId: string, ev: OnboardingTraceInput): string {
  return [
    flowId,
    ev.event,
    ev.screen ?? '',
    ev.step ?? '',
    ev.result ?? '',
    ev.error ?? '',
    payloadSignature(ev.payload),
  ].join('|');
}

function cleanupRecentEventKeys(now: number): void {
  if (recentEventKeys.size <= RECENT_EVENT_KEYS_MAX) return;
  for (const [key, ts] of recentEventKeys.entries()) {
    if (now - ts > DEDUPE_WINDOW_MS) {
      recentEventKeys.delete(key);
    }
  }
}

function shouldDropEvent(flowId: string, ev: OnboardingTraceInput): boolean {
  if (ev.event === 'onboarding_started') {
    if (startedFlowIds.has(flowId)) return true;
    startedFlowIds.add(flowId);
    return false;
  }

  const now = Date.now();
  const dedupeKey = buildEventDedupeKey(flowId, ev);
  const lastTs = recentEventKeys.get(dedupeKey);
  const windowMs = ev.event === 'react_error_boundary' ? REACT_ERROR_DEDUPE_WINDOW_MS : DEDUPE_WINDOW_MS;
  if (lastTs && now - lastTs < windowMs) {
    return true;
  }
  recentEventKeys.set(dedupeKey, now);
  cleanupRecentEventKeys(now);
  return false;
}

async function postEvents(events: Array<OnboardingTraceInput & { ts?: string }>): Promise<void> {
  if (!init || events.length === 0) return;
  const { anonId, flowId } = getIds();
  const body = {
    anonId,
    flowId,
    userId: init.getUserId(),
    app: init.app,
    build: init.getBuild(),
    events: events.map((e) => ({
      ts: e.ts,
      screen: e.screen,
      step: e.step,
      event: e.event,
      result: e.result,
      payload: e.payload ?? null,
      error: e.error ?? null,
    })),
  };
  const base = init.getApiBaseUrl().replace(/\/$/, '');
  const url = `${base}/api/v1/public/onboarding-trace`;
  try {
    const res = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
      keepalive: true,
    });
    if (res.status === 401 || res.status === 403) {
      traceEndpointAuthRejected = true;
      if (typeof console !== 'undefined' && !traceAuthRejectedWarned) {
        traceAuthRejectedWarned = true;
        console.warn(
          `[onboardingTrace] ${res.status} on ${url} — трейс отключён до перезагрузки страницы (продуктовый онбординг от этого не зависит).`,
        );
      }
    }
  } catch {
    // сеть — молча
  }
}

async function flushOutbox(): Promise<void> {
  if (outbox.length === 0) return;
  const chunk = outbox.splice(0, outbox.length);
  const payload = chunk.map((e) => ({
    ...e,
    ts: e.clientTs,
  }));
  await postEvents(payload);
}

function scheduleFlush(): void {
  if (debounceTimer) clearTimeout(debounceTimer);
  debounceTimer = setTimeout(() => {
    debounceTimer = null;
    void flushOutbox();
  }, DEBOUNCE_MS);
}

/** Обычное событие: в кольцо + отложенный flush */
export function traceOnboarding(ev: OnboardingTraceInput): void {
  if (!init) return;
  const { flowId } = getIds();
  if (shouldDropEvent(flowId, ev)) return;
  const clientTs = new Date().toISOString();
  const full: StoredEvent = { ...ev, clientTs };
  pushRing(full);
  outbox.push(full);
  scheduleFlush();
}

/** Сразу на сервер (без debounce) */
export function traceOnboardingImmediate(ev: OnboardingTraceInput): void {
  if (!init) return;
  const { flowId } = getIds();
  if (shouldDropEvent(flowId, ev)) return;
  const clientTs = new Date().toISOString();
  const full: StoredEvent = { ...ev, clientTs };
  pushRing(full);
  void postEvents([{ ...ev, ts: clientTs }]);
}

/**
 * Ошибка: сначала сбрасываем outbox, затем пишем снимок кольца (до 30) + событие ошибки одним батчем.
 */
export async function traceOnboardingErrorTail(ev: OnboardingTraceInput): Promise<void> {
  if (!init) return;
  const { flowId } = getIds();
  if (shouldDropEvent(flowId, ev)) return;
  await flushOutbox();
  const clientTs = new Date().toISOString();
  const snapshot = ring.map((r) => ({
    screen: r.screen,
    step: r.step,
    event: r.event,
    result: r.result,
    payload: r.payload ?? null,
    error: r.error ?? null,
    ts: r.clientTs,
  }));
  await postEvents([...snapshot, { ...ev, ts: clientTs }]);
}

export function traceOnboardingApiError(payload: {
  path: string;
  status: number;
  message: string;
  code?: string;
}): void {
  void traceOnboardingErrorTail({
    event: 'api_error',
    result: 'error',
    screen: 'api',
    step: payload.path,
    payload: { path: payload.path, status: payload.status, code: payload.code ?? null },
    error: payload.message,
  });
}

/** Лимиты под бэкенд (zod) и разумный размер тела запроса */
const REACT_ERR_MSG_MAX = 2000;
const REACT_STACK_MAX = 6000;
const REACT_COMP_STACK_MAX = 4000;

/**
 * Падение в React ErrorBoundary → тот же JSONL, что и онбординг (`user_{anonId}.jsonl`),
 * событие `react_error_boundary` + снимок кольца (до 30 последних шагов).
 * Без initOnboardingTrace() — no-op (например лендинг без bootstrap).
 */
export function traceReactErrorBoundary(
  error: Error,
  errorInfo: { componentStack?: string | null | undefined }
): void {
  const msg = (error?.message != null && error.message !== '' ? error.message : String(error)).slice(
    0,
    REACT_ERR_MSG_MAX
  );
  const pageUrl =
    typeof window !== 'undefined' && typeof window.location?.href === 'string'
      ? window.location.href
      : null;
  void traceOnboardingErrorTail({
    event: 'react_error_boundary',
    result: 'error',
    screen: 'error_boundary',
    step: 'render',
    error: msg,
    payload: {
      name: error?.name ?? 'Error',
      stack: (error?.stack ?? '').slice(0, REACT_STACK_MAX),
      componentStack: (errorInfo?.componentStack ?? '').trim().slice(0, REACT_COMP_STACK_MAX),
      href: pageUrl,
    },
  });
}
